import { NoDataActionType } from '../interfaces/no-data';
import { ITableTdItems, IRowItems } from '../interfaces/card-data';
import { ICardlistData } from '../components/card-list/card-list.component';
import { ReusableFunctions } from './reusable-functions';
import { DomSanitizer } from '@angular/platform-browser';

export interface IScorecardKPI {
    clientKPIMatrixDescription: string;
    weight: number;
    monthlyValue: number;
    previousMonthlyValue: number;
    monthlyDiff: number;
    precision: number;
    isPercentage: boolean;
    aggDiff: number;
    aggValue: number;
    target: number;
    previousAggValue: number;
    format: 'number' | 'currency' | 'percentage' | 'string';
    bestScore: 'Lowest' | 'Highest';
}

export interface IScorecardKpiParams {
    cardId: string;
    title: string;
    data: any;
    action: NoDataActionType;
    searchTerm: string;
    cardDrilldownUrl: string;
    hasAccess?: boolean;
    hideViewAll?: boolean;
    isDataReady?: boolean;
    hasData?: boolean;
    styleSet?: object;
}

export class ScorecardKpicardDataHandler {
    constructor(private _reusableFunctions: ReusableFunctions, private _sanitizer: DomSanitizer) {}

    initializeCards(params: IScorecardKpiParams): ICardlistData {
        return {
            cardId: params.cardId,
            cardTitle: params.title,
            dataTableData: [],
            hasData: false,
            routerPath: params.cardDrilldownUrl,
            searchTerm: params.searchTerm,
            action: params.action,
            outerTableHeight: '440px',
            hideViewAll: params.hideViewAll ? params.hideViewAll : true,
            isDataReady: params.isDataReady ? params.isDataReady : false,
        };
    }

    handleKPILargeCards(
        largeCard: ICardlistData,
        data: IScorecardKPI[],
        fromDatePeriod: string,
        kpiTableColumnInf: ITableTdItems[] = []
    ) {
        let kpiTableInfo: ITableTdItems[] = [];
        if (kpiTableColumnInf.length > 0) {
            kpiTableInfo = kpiTableColumnInf;
        } else {
            kpiTableInfo = [
                {
                    headerTitle: '',
                    fieldId: 'weight',
                    isHyperlinked: false,
                    isIconColumn: true,
                    iconClass: 'fas fa-weight-hanging',
                    headerStyle: {
                        'text-align': 'center',
                        color: '#333333',
                    },
                    style: {
                        'font-weight': '500',
                        'text-align': 'center',
                        'font-size': '12px',
                        'padding-right': '8px',
                        width: '3%',
                    },
                },
                {
                    headerTitle: 'KPI',
                    fieldId: 'clientKPIMatrixDescription',
                    isHyperlinked: false,
                    headerStyle: {
                        'text-align': 'left',
                        color: '#333333',
                    },
                    style: {
                        'font-weight': '500',
                        'text-align': 'left',
                        'font-size': '14px',
                        width: '20%',
                    },
                },
                {
                    headerTitle: 'Target',
                    fieldId: 'target',
                    isHyperlinked: false,
                    headerStyle: {
                        'text-align': 'right',
                        color: '#333333',
                    },
                    style: {
                        'text-align': 'right',
                        'font-size': '14px',
                        width: '12%',
                    },
                },
                {
                    headerTitle: 'MTD',
                    fieldId: 'monthlyValue',
                    isHyperlinked: false,
                    headerStyle: {
                        'text-align': 'right',
                        color: '#333333',
                    },
                    style: {
                        'text-align': 'right',
                        'font-size': '14px',
                        width: '13%',
                    },
                },
                {
                    headerTitle: 'Prior MTD',
                    fieldId: 'monthlyValue',
                    isHyperlinked: false,
                    headerStyle: {
                        'text-align': 'right',
                        color: '#333333',
                    },
                    style: {
                        'text-align': 'right',
                        'font-size': '14px',
                        width: '14%',
                    },
                    showIndicator: true,
                    compareFieldId: 'previousMonthlyValue',
                    diffField: 'monthlyDiff',
                },
                {
                    headerTitle: fromDatePeriod === '12m' ? '12mo' : 'YTD',
                    fieldId: 'aggValue',
                    isHyperlinked: false,
                    headerStyle: {
                        'text-align': 'right',
                        color: '#333333',
                    },
                    style: {
                        'text-align': 'right',
                        'font-size': '14px',
                        width: '14%',
                    },
                },
                {
                    headerTitle: fromDatePeriod === '12m' ? 'Prev 12mo' : 'Prev YTD',
                    fieldId: 'aggValue',
                    isHyperlinked: false,
                    headerStyle: {
                        'text-align': 'right',
                        color: '#333333',
                    },
                    style: {
                        'text-align': 'right',
                        'font-size': '14px',
                        width: '14%',
                    },
                    showIndicator: true,
                    compareFieldId: 'previousAggValue',
                    diffField: 'aggDiff',
                },
            ];
        }
        let hasData = false;
        if (data.length > 0) {
            hasData = true;
            let tableRowItems: IRowItems[] = [];
            data.forEach((item) => {
                let dataItem = Object.assign({}, item);
                let tableTdItems: ITableTdItems[] = [];
                if (kpiTableInfo) {
                    kpiTableInfo.forEach((rowInf) => {
                        let rowInfCopy = Object.assign({}, rowInf);
                        let value = dataItem[rowInfCopy.fieldId];
                        rowInfCopy.headerStyle['text-transform'] = 'uppercase';

                        if (value != null && typeof value === 'number') {
                            if (rowInfCopy.showIndicator) {
                                let comparisonInf = this._reusableFunctions.calculateComparisonPercentage(
                                    dataItem[rowInfCopy.fieldId],
                                    dataItem[rowInfCopy.compareFieldId]
                                );
                                let caretInfoMonthly = this._reusableFunctions.getCaret(
                                    comparisonInf.percentage,
                                    dataItem.bestScore === 'Lowest' ? true : false,
                                    null,
                                    comparisonInf.isComparable,
                                    2
                                );
                                rowInfCopy.indicatorIcon = caretInfoMonthly.ng_caret_icon;
                                rowInfCopy.indicatorColor = caretInfoMonthly.color;
                                rowInfCopy.svgData = this._sanitizer.bypassSecurityTrustHtml(
                                    caretInfoMonthly.svgData
                                );
                                value = caretInfoMonthly.comparison;
                            } else if (
                                dataItem.format === 'currency' &&
                                (rowInfCopy.headerTitle.toLowerCase() === 'mtd' ||
                                    rowInfCopy.headerTitle.toLowerCase() === 'ytd' ||
                                    rowInfCopy.headerTitle.toLowerCase() === '12mo' ||
                                    rowInfCopy.fieldId.toLowerCase() === 'target')
                            ) {
                                value = this._reusableFunctions.formatMoney(
                                    dataItem[rowInfCopy.fieldId],
                                    dataItem.precision
                                );
                            } else {
                                let isNegative = value < 0;

                                if (isNegative) {
                                    value = Math.abs(value);
                                }

                                value = value.toLocaleString(undefined, {
                                    maximumFractionDigits: dataItem.precision,
                                    minimumFractionDigits: dataItem.precision,
                                });

                                if (dataItem.isPercentage || dataItem.format == 'percentage') {
                                    value = value + '%';
                                }

                                if (isNegative) {
                                    value = '(' + value + ')';
                                }
                            }
                        }
                        rowInfCopy.value = value;

                        if (rowInfCopy.isHyperlinked) {
                            rowInfCopy.urlSegment = '';
                            rowInfCopy.routerLink = '';
                            rowInfCopy.hasAccess = false;
                            // TODO: add Access if drilldowns available
                            //this._userAccessService.hasAccessToPage(
                            //rowInfCopy.routerLink + rowInfCopy.urlSegment
                            //);
                        }

                        if (rowInfCopy.isIconColumn) {
                            rowInfCopy.iconClass =
                                dataItem[rowInfCopy.fieldId] && dataItem[rowInfCopy.fieldId] > 0
                                    ? rowInfCopy.iconClass
                                    : '';
                        }
                        tableTdItems.push(rowInfCopy);
                    });

                    let weightCellInf = tableTdItems.find((item) => item.fieldId === 'weight');
                    if (weightCellInf && weightCellInf.value) {
                        let clientKPIMatrixDescriptionInf = tableTdItems.find(
                            (item) => item.fieldId === 'clientKPIMatrixDescription'
                        );
                        if (clientKPIMatrixDescriptionInf && clientKPIMatrixDescriptionInf.value) {
                            weightCellInf.tooltipText =
                                clientKPIMatrixDescriptionInf.value +
                                ' is configured as ' +
                                parseFloat(weightCellInf.value).toFixed(0) +
                                '% of this weighted score.';
                        }
                    }

                    tableRowItems.push({
                        tdList: tableTdItems,
                    });
                }
            });

            largeCard.dataTableData = tableRowItems;
        }

        largeCard.hasData = hasData;
        largeCard.isDataReady = true;
        return Object.assign({}, largeCard);
    }
}
